import React, { useCallback, useContext, useState } from "react";
import { Box, Button, Grid, Divider, Stack } from "@mui/material";
import { Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";

import boxStyles from "../../../styles/boxing";
import MainTitle from "../../../Components/Common/MainTitle/MainTitle";
import TextFieldRenderer from "../../../Components/Form/TextFieldRenderer";

import { DELETE_FIELD, GET_FIELDS } from "../../../graphql/fieldGraphql";

import { useTranslation } from "../../../context/Internationalization";
import { SnackBarContext } from "../../../context/SnackBarContext";

import useInfinityScroll from "../../../Hooks/useInfinityScroll";
import useGraphql from "../../../Hooks/useGraphql";
import useLog from "../../../Hooks/useLog";
import { useDebouncedSearch } from "../../../Hooks/useDebouncedSearch";

import FieldsTable from "./FieldsTable/FieldsTable";
import FieldsDrawer from "./FieldsDrawer/FieldsDrawer";
import DeleteConfirmationModal from "./Components/DeleteConfirmationModal/DeleteConfirmationModal";

const tableMenuActions = (handleAction, t) => [
  {
    label: t("edit_field"),
    icon: <EditIcon fontSize="small" />,
    onClickAction: (field) => handleAction("edit", field),
  },
  {
    label: t("delete_field"),
    icon: <DeleteIcon sx={{ color: "red" }} fontSize="small" />,
    onClickAction: (field) => handleAction("delete", field),
  },
];

const SuperAdminFields = () => {
  const { t } = useTranslation();
  const snackBar = useContext(SnackBarContext);
  const { axiosGraphQL } = useGraphql();
  const { errorLog } = useLog();

  const [searchTerm, setSearchTerm] = useState("");
  const [extraFields, setExtraFields] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [state, setState] = useState({
    drawer: false,
    deleteModal: false,
    selectedField: null,
    action: "",
    loading: false,
  });

  const fetchFields = useCallback(
    async (term) => {
      const res = await axiosGraphQL({
        query: GET_FIELDS,
        variables: { term, skip: 0, limit: 20 },
      });
      const results = res.data.data.getFields || [];

      setExtraFields([]);
      setHasMore(results.length === 20);
      return results;
    },
    [axiosGraphQL]
  );

  const {
    options: fields,
    loading,
    debouncedTerm,
    refetch,
  } = useDebouncedSearch({
    searchTerm,
    fetchOptions: fetchFields,
  });

  const loadMoreFields = useCallback(async () => {
    try {
      const currentSkip = fields.length + extraFields.length;

      const response = await axiosGraphQL({
        query: GET_FIELDS,
        variables: {
          term: debouncedTerm,
          skip: currentSkip,
          limit: 20,
        },
      });

      const more = response.data.data.getFields || [];

      const existingIds = new Set([
        ...fields.map((f) => f._id),
        ...extraFields.map((f) => f._id),
      ]);
      const newFields = more.filter((f) => !existingIds.has(f._id));

      setExtraFields((prev) => [...prev, ...newFields]);
      setHasMore(more.length === 20); // Stop when results are < limit
    } catch (err) {
      errorLog(err, "SuperAdminFields", "loadMoreFields", "GET_FIELDS");
    }
  }, [debouncedTerm, fields, extraFields, axiosGraphQL]);

  const lastElementRef = useInfinityScroll({
    updatePage: loadMoreFields,
    isLoading: loading,
    hasMore,
  });

  const allFields = [
    ...new Map([...fields, ...extraFields].map((f) => [f._id, f])).values(),
  ];

  const onChangeFilter = ({ target: { name, value } }) => {
    if (name === "term") {
      setSearchTerm(value);
    }
  };

  const handleOpenDrawer = () => {
    setState((prev) => ({ ...prev, drawer: true }));
  };

  const handleCloseDrawer = () => {
    setState((prev) => ({ ...prev, drawer: false, selectedField: null }));
  };

  const handleAction = (action, field) => {
    if (action === "delete") {
      setState((prev) => ({
        ...prev,
        action,
        deleteModal: true,
        selectedField: field,
      }));
    } else {
      setState((prev) => ({
        ...prev,
        action,
        drawer: true,
        selectedField: field,
      }));
    }
  };

  const handleCloseDeleteModal = () => {
    setState((prev) => ({ ...prev, deleteModal: false, selectedField: null }));
  };

  const handleDeleteField = async () => {
    try {
      setState((prev) => ({ ...prev, loading: true }));

      await axiosGraphQL({
        query: DELETE_FIELD,
        variables: { fieldId: state.selectedField?._id },
      });

      snackBar.setOptions({
        alertMessage: `${t("the_field")} ${t("deleted_successfully")}`,
        alertSeverity: "success",
      });
      snackBar.setSnackbarOpen(true);
      refetch();
    } catch (error) {
      errorLog(error, "SuperAdminFields", "handleDeleteField", "DELETE_FIELD");
      snackBar.setOptions({
        alertMessage: error.message,
        alertSeverity: "error",
      });
      snackBar.setSnackbarOpen(true);
    } finally {
      setState((prev) => ({
        ...prev,
        loading: false,
        deleteModal: false,
        selectedField: null,
      }));
    }
  };

  return (
    <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box sx={boxStyles.headerContainer} component="div">
            <Stack direction="row" spacing={2}>
              <MainTitle title={t("fields_admin_panel")} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Button type="button" size="small" onClick={handleOpenDrawer} variant="contained" color="primary">
                {t("new_field")}
              </Button>
            </Stack>
          </Box>
          <Divider />
        </Grid>

        <Grid item xs={12} style={boxStyles.filtersContainer}>
          <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <TextFieldRenderer
                label={t("search")}
                onChange={onChangeFilter}
                name="term"
                value={searchTerm}
              />
            </Grid>
          </Stack>
        </Grid>

        <Grid item xs={12}>
          {allFields.length === 0 && !loading ? (
            <span>{t("no_fields_found")}</span>
          ) : (
            <FieldsTable
              lastElementRef={lastElementRef}
              fields={allFields}
              menuActions={tableMenuActions(handleAction, t)}
            />
          )}
        </Grid>
      </Grid>

      <FieldsDrawer
        openDrawer={state.drawer}
        onCloseDrawer={handleCloseDrawer}
        selectedField={state.selectedField}
        getFields={() => refetch()}
      />

      <DeleteConfirmationModal
        open={state.deleteModal}
        onClose={handleCloseDeleteModal}
        onDelete={handleDeleteField}
      />
    </Box>
  );
};

export default SuperAdminFields;
